/**
 * FormerWorkers — migrated from mockData to live svdy-platform API.
 *
 * Thin derivative of ActiveWorkers: filter on `status='terminated'`
 * and render a read-only table with two actions per row:
 *
 *   - Reinstate → flip status back to 'active' (moves the record back
 *     to ActiveWorkers, history preserved).
 *   - Delete → removes the RECORD (ADR-049 axis 2). Soft, and
 *     reversible from the "Deleted records" view below.
 *
 * THIS COMMENT USED TO SAY "permanent hard-delete; fails if there are
 * dependent records like time entries", and neither half was true —
 * `delete_worker` set `status='former'` and performed no dependency
 * check at all. `former` was read by NO list (this page filters
 * `terminated`), so the worker silently disappeared from the admin UI
 * while SCIM still returned them to the IdP. The frontend's MODEL was
 * right and the backend disagreed with it; ADR-049 implemented this
 * model rather than inventing a new one. Hard delete stays rejected on
 * measured grounds: 37 of the 57 foreign keys into `workers` are
 * ON DELETE NO ACTION, so the database already refuses to purge anyone
 * with history, and branching on that would give one button two
 * irreversible outcomes an admin cannot predict.
 *
 * The backend's WorkerResponse carries `termination_date` (set by the
 * server when status transitions to terminated) so the column displays
 * it directly. The mock carried a `reason_for_leaving` field that the
 * server doesn't expose — dropped rather than smuggled into some
 * description column. Adding reason requires a backend schema change
 * first.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/RestoreFromTrash';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog, ExportButton } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';

type Worker = Schema<'WorkerResponse'>;
type Department = Schema<'DepartmentResponse'>;
type Position = Schema<'PositionResponse'>;
type Location = Schema<'LocationResponse'>;

const STATUS_TERMINATED = 'terminated';
const STATUS_ACTIVE = 'active';

const FormerWorkers: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Pending reinstate target — drives the ConfirmDialog below.
  const [pendingReinstate, setPendingReinstate] = useState<Worker | null>(
    null,
  );

  /** ADR-049 axis 2. `false` = terminated records (this page's subject); `true` = the removed
   *  ones. Without this view a soft delete would be unrecoverable from the UI, which makes it
   *  strictly worse than a hard delete — see rule 4. */
  const [showDeleted, setShowDeleted] = useState(false);
  const [toRestore, setToRestore] = useState<Worker | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [toDelete, setToDelete] = useState<Worker | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [locFilter, setLocFilter] = useState<string>('all');
  const [deptFilter, setDeptFilter] = useState<string>('all');

  // Row kebab menu state (matches ActiveWorkers pattern).
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<Worker | null>(null);
  const navigate = useNavigate();

  const openRowMenu = (target: Worker, anchor: HTMLElement) => {
    setMenuTarget(target);
    setMenuAnchor(anchor);
  };
  const closeRowMenu = () => setMenuAnchor(null);

  const handleViewAttendance = () => {
    if (!menuTarget) return;
    const query = new URLSearchParams();
    query.set('worker', `${menuTarget.first_name} ${menuTarget.last_name}`);
    closeRowMenu();
    navigate(`/attendance/time-entries?${query.toString()}`);
  };


  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [workers, deps, poss, locs] = await Promise.all([
        // `deleted=true` returns removed records regardless of status, so the status filter is
        // dropped in that view — a deleted `invited` typo row must be restorable too.
        showDeleted
          ? organizationApi.workersApi.list(undefined, true)
          : organizationApi.workersApi.list(STATUS_TERMINATED),
        organizationApi.departmentsApi.list(),
        organizationApi.positionsApi.list(),
        organizationApi.locationsApi.list(),
      ]);
      setRows(workers);
      setDepartments(deps);
      setPositions(poss);
      setLocations(locs);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:formerWorkers.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t, showDeleted]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ------------------------------------------------------------------
  // FK lookup maps
  // ------------------------------------------------------------------

  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) m.set(d.id, d.name);
    return m;
  }, [departments]);

  const posName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of positions) m.set(p.id, p.title);
    return m;
  }, [positions]);

  // ------------------------------------------------------------------
  // Summary
  // ------------------------------------------------------------------

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const now = new Date();
    const m = now.getMonth();
    const y = now.getFullYear();
    const terminatedThisMonth = rows.filter((r) => {
      if (!r.termination_date) return false;
      const d = new Date(r.termination_date);
      return d.getMonth() === m && d.getFullYear() === y;
    }).length;
    const withEmail = rows.filter((r) => !!r.email).length;
    return { total, terminatedThisMonth, withEmail };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:formerWorkers.stats.total'),
        description: t('org:formerWorkers.stats.totalDescription'),
        avatar: {
          icon: <PeopleAltOutlinedIcon />,
          bgcolor: 'grey.200',
          color: 'text.secondary',
        },
      },
      {
        id: 'thismonth',
        value: summaryStats.terminatedThisMonth,
        title: t('org:formerWorkers.stats.thisMonth'),
        description: t('org:formerWorkers.stats.thisMonthDescription'),
        avatar: {
          icon: <HistoryIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      {
        id: 'withemail',
        value: summaryStats.withEmail,
        title: t('org:formerWorkers.stats.withEmail'),
        description: t('org:formerWorkers.stats.withEmailDescription'),
        avatar: {
          icon: <PersonAddAltIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
    ],
    [summaryStats, t],
  );

  // ------------------------------------------------------------------
  // Filter / pagination
  // ------------------------------------------------------------------

  const filtered = useMemo(() => {
    const scoped = rows.filter((e) => {
      if (locFilter !== 'all' && e.primary_location_id !== locFilter) return false;
      if (deptFilter !== 'all' && e.primary_department_id !== deptFilter) return false;
      return true;
    });
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter((e) => {
      const depLabel = (e.primary_department_id &&
        deptName.get(e.primary_department_id)) || '';
      const posLabel = (e.position_id && posName.get(e.position_id)) || '';
      return (
        e.worker_code.toLowerCase().includes(needle) ||
        e.first_name.toLowerCase().includes(needle) ||
        e.last_name.toLowerCase().includes(needle) ||
        (e.email ?? '').toLowerCase().includes(needle) ||
        depLabel.toLowerCase().includes(needle) ||
        posLabel.toLowerCase().includes(needle)
      );
    });
  }, [rows, filterText, locFilter, deptFilter, deptName, posName]);

  const paginated = useMemo(
    () =>
      filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const handleReinstate = async (e: Worker) => {
    setSaving(true);
    try {
      await organizationApi.workersApi.update(e.id, {
        status: STATUS_ACTIVE,
      });
      setToast(
        t('org:formerWorkers.toast.reinstated', { name: `${e.first_name} ${e.last_name}` }),
      );
      await fetchAll();
    } catch (err) {
      const message =
        err instanceof ApiError ? translateApiError(err, t) : t('org:formerWorkers.toast.reinstateFailed');
      setToast(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (e: Worker) => {
    setToDelete(e);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleRestore = async () => {
    if (!toRestore) return;
    setSaving(true);
    try {
      await organizationApi.workersApi.restore(toRestore.id);
      setToast(
        t('org:formerWorkers.toastRestored', { name: `${toRestore.first_name} ${toRestore.last_name}` }),
      );
      setToRestore(null);
      await fetchAll();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:formerWorkers.toastRestoreFailed'),
      );
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.workersApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(
        t('org:formerWorkers.toast.deleted', { name: `${toDelete.first_name} ${toDelete.last_name}` }),
      );
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:formerWorkers.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:formerWorkers.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.workersApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:formerWorkers.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:formerWorkers.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // ------------------------------------------------------------------
  // Row selection / pagination
  // ------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  // Client-side CSV export — same pattern as ActiveWorkers.
  // Records export, not a round-trip file. Former workers are terminated: nobody
  // re-imports them, but FLSA keeps time records two years and payroll three (four in
  // California), so this file is what answers a records request. Headers are localised
  // because a person reads it.
  //
  // Replaces a hand-rolled CSV whose escaping was correct but which omitted the UTF-8
  // BOM, so Excel garbled every non-ASCII name — on a product shipping in nine
  // languages that was most of the value of the export.
  const exportColumns = useMemo(() => [
    t('org:formerWorkers.table.code'),
    t('org:formerWorkers.table.name'),
    t('org:formerWorkers.table.email'),
    t('org:formerWorkers.table.phone'),
    t('org:formerWorkers.table.department'),
    t('org:formerWorkers.table.position'),
    t('org:formerWorkers.table.hireDate'),
    t('org:formerWorkers.table.terminated'),
  ], [t]);

  const exportRows = useMemo(() => filtered.map((e) => ({
    [exportColumns[0]]: e.worker_code ?? '',
    [exportColumns[1]]: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim(),
    [exportColumns[2]]: e.email ?? '',
    [exportColumns[3]]: e.phone ?? '',
    [exportColumns[4]]: e.primary_department_id ? deptName.get(e.primary_department_id) ?? '' : '',
    [exportColumns[5]]: e.position_id ? posName.get(e.position_id) ?? '' : '',
    [exportColumns[6]]: e.hire_date ?? '',
    [exportColumns[7]]: e.termination_date ?? '',
  })), [filtered, exportColumns, deptName, posName]);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((e) => e.id) : []);
  };

  // ------------------------------------------------------------------
  // Render guards
  // ------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem' }}
          >
            {t('org:formerWorkers.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:formerWorkers.subtitle')}
          </Typography>
        </Box>
        <Box>
          {/* ADR-049 axis 2. The view that makes a soft delete recoverable — without it the
              records this page can delete would be listed by nothing and restorable by nobody,
              i.e. destroyed with extra steps (rule 4). */}
          <Button
            size="small"
            variant={showDeleted ? 'contained' : 'outlined'}
            onClick={() => { setSelectedIds([]); setShowDeleted((v) => !v); }}
            sx={{ mr: 1, textTransform: 'none', fontSize: '0.8rem' }}
          >
            {t('org:formerWorkers.deletedToggle')}
          </Button>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ mr: 1 }}>
            <ExportButton
              filename={`former-workers-${new Date().toISOString().slice(0, 10)}`}
              title={t('org:formerWorkers.title')}
              columns={exportColumns}
              rows={exportRows}
              onDone={() => setToast(t('org:formerWorkers.toast.exportDone', { count: exportRows.length }))}
              onError={(m) => setToast(m)}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3.
          Location (Where) → Department (Who) → Search (Who free-text). */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('org:formerWorkers.filters.locationLabel')}</InputLabel>
          <Select
            value={locFilter}
            label={t('org:formerWorkers.filters.locationLabel')}
            onChange={(e) => { setLocFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:formerWorkers.filters.allLocations')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('org:formerWorkers.filters.departmentLabel')}</InputLabel>
          <Select
            value={deptFilter}
            label={t('org:formerWorkers.filters.departmentLabel')}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:formerWorkers.filters.allDepartments')}</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label={t('org:formerWorkers.searchLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 320, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 — active non-default filters, individually clearable. */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (locFilter !== 'all') {
          const name = locations.find((l) => l.id === locFilter)?.name ?? locFilter;
          chips.push({
            key: 'loc',
            label: t('org:formerWorkers.filters.locationChip', { name }),
            onClear: () => { setLocFilter('all'); setPage(0); },
          });
        }
        if (deptFilter !== 'all') {
          const name = deptName.get(deptFilter) ?? deptFilter;
          chips.push({
            key: 'dept',
            label: t('org:formerWorkers.filters.departmentChip', { name }),
            onClear: () => { setDeptFilter('all'); setPage(0); },
          });
        }
        if (filterText.trim()) {
          chips.push({
            key: 'search',
            label: t('org:formerWorkers.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); },
          });
        }
        if (chips.length === 0) return null;
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {chips.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                onDelete={c.onClear}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setLocFilter('all');
                setDeptFilter('all');
                setFilterText('');
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                minWidth: 0,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}
            >
              {t('org:formerWorkers.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText || locFilter !== 'all' || deptFilter !== 'all'
              ? t('org:formerWorkers.empty.noMatches')
              : t('org:formerWorkers.empty.noResults')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 900 }} aria-label={t('org:formerWorkers.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:formerWorkers.table.code')}</TableCell>
                <TableCell>{t('org:formerWorkers.table.name')}</TableCell>
                <TableCell>{t('org:formerWorkers.table.department')}</TableCell>
                <TableCell>{t('org:formerWorkers.table.position')}</TableCell>
                <TableCell>{t('org:formerWorkers.table.hireDate')}</TableCell>
                <TableCell>{t('org:formerWorkers.table.terminated')}</TableCell>
                <TableCell align="center">{t('org:formerWorkers.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((worker) => {
                const selected = isSelected(worker.id);
                return (
                  <TableRow
                    hover
                    key={worker.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, worker.id)}
                        inputProps={{ 'aria-label': `${worker.first_name} ${worker.last_name}` }}
                      />
                    </TableCell>
                    <TableCell>{worker.worker_code}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {worker.first_name} {worker.last_name}
                        </Typography>
                        {worker.email && (
                          <Typography variant="caption" color="text.secondary">
                            {worker.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {worker.primary_department_id
                        ? deptName.get(worker.primary_department_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {worker.position_id
                        ? posName.get(worker.position_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>{worker.hire_date ?? '—'}</TableCell>
                    <TableCell>{worker.termination_date ?? '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('org:formerWorkers.tooltips.reinstate')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingReinstate(worker);
                          }}
                        >
                          <PersonAddAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('org:formerWorkers.tooltips.more')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRowMenu(worker, e.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={!!toRestore}
        title={t('org:formerWorkers.restoreTitle')}
        description={t('org:formerWorkers.restoreDescription')}
        subject={
          toRestore && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {toRestore.first_name} {toRestore.last_name}
            </Box>
          )
        }
        confirmLabel={t('org:formerWorkers.restoreConfirm')}
        onConfirm={handleRestore}
        onClose={() => setToRestore(null)}
      />

      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:formerWorkers.deleteTitle')}
        description={t('org:formerWorkers.deleteDescription')}
        subject={
          toDelete && (
            <>
              <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {toDelete.first_name} {toDelete.last_name}
              </Box>
              <Box
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {toDelete.worker_code}
                {toDelete.email ? ` · ${toDelete.email}` : ''}
              </Box>
            </>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      {/* Row action menu — hosts the actions trimmed from the table cell
          plus cross-module "View attendance" / Export. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleViewAttendance}>
          <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:formerWorkers.menu.viewAttendance')}
            secondary={t('org:formerWorkers.menu.viewAttendanceHint')}
          />
        </MenuItem>
        {/* ADR-049 axis 2. Restore is the ONLY row action in the deleted view — the dialog
            without this trigger would be a component that exists and can never be reached
            (svdy-ui §12.12). Delete is hidden there because the record is already deleted, and
            a second DELETE is a 404. */}
        {showDeleted && (
          <MenuItem
            onClick={() => { if (menuTarget) setToRestore(menuTarget); closeRowMenu(); }}
          >
            <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={t('org:formerWorkers.restoreButton')}
              secondary={t('org:formerWorkers.restoreDescription')}
            />
          </MenuItem>
        )}
        {!showDeleted && (
        <MenuItem
          onClick={() => {
            if (menuTarget) handleOpenDeleteDialog(menuTarget);
            closeRowMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText
            primary={t('org:formerWorkers.menu.deletePermanent')}
            secondary={t('org:formerWorkers.menu.deletePermanentHint')}
          />
        </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>

      {/* Reinstate confirmation — replaces the old window.confirm() call.
          Primary tone because reinstating is a restorative action, not
          destructive. */}
      <ConfirmDialog
        open={!!pendingReinstate}
        tone="primary"
        title={t('org:formerWorkers.reinstateTitle')}
        description={t('org:formerWorkers.reinstateDescription')}
        subject={
          pendingReinstate && (
            <>
              <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {pendingReinstate.first_name} {pendingReinstate.last_name}
              </Box>
              <Box
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {pendingReinstate.worker_code}
                {pendingReinstate.email ? ` · ${pendingReinstate.email}` : ''}
              </Box>
            </>
          )
        }
        confirmLabel={t('org:formerWorkers.reinstateButton')}
        confirmLoadingLabel={t('org:formerWorkers.reinstateLoading')}
        onConfirm={() =>
          pendingReinstate
            ? handleReinstate(pendingReinstate)
            : Promise.resolve()
        }
        onClose={() => setPendingReinstate(null)}
      />
    </Box>
  );
};

export default FormerWorkers;
